import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  GoogleAuthProvider,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import { auth, googleProvider, microsoftProvider } from '../firebase';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// iOS Safari (and other browsers) with "Prevent Cross-Site Tracking" /
// storage partitioning can break the Firebase OAuth handshake because the
// auth handler lives on a different domain (firebaseapp.com). Firebase surfaces
// this as "missing initial state" or web-storage-unsupported errors.
const STORAGE_BLOCKED_MESSAGE =
  'Your browser is blocking the sign-in popup\'s storage. On iPhone/Safari, ' +
  'turn off Settings → Safari → "Prevent Cross-Site Tracking" (or disable ' +
  'Private Browsing), then try again. You can also sign in with email and password.';

// signInWithPopup is unreliable on iOS Safari (the popup gets killed and
// Firebase reports auth/popup-closed-by-user). On mobile we use the redirect
// flow instead, which is safe now that authDomain is same-site.
const prefersRedirectFlow = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    // iPadOS reports as Mac but has touch
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isStorageBlockedError = (err) => {
  const code = err?.code || '';
  const msg = (err?.message || '').toLowerCase();
  return (
    code === 'auth/web-storage-unsupported' ||
    code === 'auth/missing-or-invalid-nonce' ||
    code === 'auth/internal-error' ||
    msg.includes('missing initial state') ||
    msg.includes('sessionstorage') ||
    msg.includes('web storage') ||
    msg.includes('storage is inaccessible')
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guard to prevent onAuthStateChanged from creating the user via
  // firebase-login while a registration (firebase-register) is in progress.
  // Without this, createUserWithEmailAndPassword fires onAuthStateChanged,
  // which races firebase-register and creates the user without business fields.
  const isRegistering = useRef(false);

  // Dedupe concurrent syncs for the same user. The GIS/credential login syncs
  // explicitly AND onAuthStateChanged fires too, so without this both fire a
  // firebase-login at the same instant (which races to create the account).
  const syncingUidRef = useRef(null);

  // Sync with backend when Firebase auth state changes
  const syncWithBackend = async (firebaseUser) => {
    if (isRegistering.current) {
      // Registration flow owns user creation; skip the auto-sync.
      return;
    }

    if (!firebaseUser) {
      setUser(null);
      localStorage.removeItem('token');
      return;
    }

    if (syncingUidRef.current === firebaseUser.uid) {
      // A sync for this user is already in flight; don't double-fire.
      return;
    }
    syncingUidRef.current = firebaseUser.uid;

    try {
      // Get Firebase ID token
      const idToken = await firebaseUser.getIdToken();
      localStorage.setItem('token', idToken);

      // Sync with backend to get/create user profile
      const response = await axios.post(`${API_URL}/auth/firebase-login`, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        emailVerified: firebaseUser.emailVerified,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      setUser(response.data.user);
    } catch (err) {
      console.error('Backend sync failed:', err);
      setError(err.response?.data?.error || 'Failed to sync with backend');
    } finally {
      syncingUidRef.current = null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      // Check for existing JWT token (legacy accounts)
      const token = localStorage.getItem('token');
      if (token && !token.startsWith('eyJhbGciOiJSUzI1NiIsImtpZCI6')) {
        // Looks like a JWT token, not a Firebase token
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setUser(response.data.data);
          setFirebaseUser(null);
          setLoading(false);
          return;
        } catch (err) {
          console.error('JWT token validation failed:', err);
          localStorage.removeItem('token');
        }
      }
    };

    initAuth();

    // Handle the result of a redirect-based social sign-in (mobile flow).
    // onAuthStateChanged will also fire, but this lets us surface any error
    // that occurred during the redirect handshake.
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          // Ensure the backend account is created/synced from the redirect.
          syncWithBackend(result.user);
        }
      })
      .catch((err) => {
        console.error('Redirect sign-in error:', err);
        if (isStorageBlockedError(err)) {
          setError(STORAGE_BLOCKED_MESSAGE);
        } else if (err.code === 'auth/account-exists-with-different-credential') {
          setError('An account already exists with this email using a different sign-in method. Try email/password.');
        } else if (err.code && err.code !== 'auth/no-auth-event') {
          setError(err.message || 'Sign-in failed. Please try again.');
        }
      });

    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      await syncWithBackend(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Register with email/password
  const register = async (userData) => {
    // Take ownership of user creation so the onAuthStateChanged listener
    // does NOT race us by calling firebase-login (which would create the
    // user without business fields).
    isRegistering.current = true;
    try {
      setError(null);
      const { email, password, username, firstName, lastName, businessInfo, storeLocation, applyForBusiness } = userData;

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Send verification email
      await sendEmailVerification(userCredential.user);

      // Get ID token
      const idToken = await userCredential.user.getIdToken();

      // Create user in backend with additional data
      const backendData = {
        uid: userCredential.user.uid,
        email,
        username,
        firstName,
        lastName,
        emailVerified: false,
      };

      // Add business fields if applying for business account
      if (applyForBusiness) {
        backendData.businessInfo = businessInfo;
        backendData.storeLocation = storeLocation;
        backendData.applyForBusiness = true;
      }

      const response = await axios.post(`${API_URL}/auth/firebase-register`, backendData, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      setUser(response.data.user);
      return { success: true, message: 'Registration successful! Please check your email to verify your account.' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      // Release the guard now that firebase-register has finished (or failed).
      isRegistering.current = false;
    }
  };

  // Login with email/password
  const login = async (email, password) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged will handle the rest
      return { success: true };
    } catch (err) {
      // If Firebase login fails, try legacy JWT login for existing accounts
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        try {
          console.log('Trying legacy JWT login for existing account...');
          const response = await axios.post(`${API_URL}/auth/login`, { email, password });
          const { token, user: userData } = response.data;
          localStorage.setItem('token', token);
          setUser(userData);
          setFirebaseUser(null);
          return { success: true, message: 'Logged in with legacy account' };
        } catch (jwtErr) {
          // Both Firebase and JWT login failed
          const errorMessage = jwtErr.response?.data?.error || 'Invalid email or password';
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }
      }

      // Other Firebase errors
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setError(null);
      if (prefersRedirectFlow()) {
        // Mobile: redirect away to the (now same-site) auth handler. Result is
        // handled by getRedirectResult/onAuthStateChanged when we come back.
        await signInWithRedirect(auth, googleProvider);
        return { success: true };
      }
      const result = await signInWithPopup(auth, googleProvider);
      // Sync immediately from the popup result. On iOS Safari the
      // onAuthStateChanged listener in the opener can fail to fire after the
      // popup closes, which previously meant the backend account was never
      // created. Syncing here guarantees it (find-or-create is idempotent).
      await syncWithBackend(result.user);
      return { success: true };
    } catch (err) {
      console.error('Google login error:', err);

      // User-friendly error messages
      let errorMessage = 'Google login failed. Please try again.';

      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login cancelled. Please try again if you want to sign in.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by your browser. Please allow popups and try again.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Login cancelled. Please try again.';
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different sign-in method. Try signing in with email/password or Microsoft.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (isStorageBlockedError(err)) {
        errorMessage = STORAGE_BLOCKED_MESSAGE;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with a Google ID token obtained via Google Identity Services (GIS).
  // GIS runs entirely on our own origin and returns a token directly, so there
  // is no Firebase redirect/popup handler for iOS Safari ITP to block. This is
  // the primary Google sign-in path; signInWithPopup/Redirect stay as fallback.
  const loginWithGoogleIdToken = async (idToken) => {
    try {
      setError(null);
      const credential = GoogleAuthProvider.credential(idToken);
      const result = await signInWithCredential(auth, credential);
      await syncWithBackend(result.user);
      return { success: true };
    } catch (err) {
      console.error('Google credential login error:', err);
      let errorMessage = 'Google login failed. Please try again.';
      if (err.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different sign-in method. Try email/password or Microsoft.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Login with Microsoft
  const loginWithMicrosoft = async () => {
    try {
      setError(null);
      if (prefersRedirectFlow()) {
        await signInWithRedirect(auth, microsoftProvider);
        return { success: true };
      }
      const result = await signInWithPopup(auth, microsoftProvider);
      // Sync immediately from the popup result (see Google note above) so the
      // backend account is created even if onAuthStateChanged doesn't fire.
      await syncWithBackend(result.user);
      return { success: true };
    } catch (err) {
      console.error('Microsoft login error:', err);

      // User-friendly error messages
      let errorMessage = 'Microsoft login failed. Please try again.';

      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login cancelled. Please try again if you want to sign in.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked by your browser. Please allow popups and try again.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Login cancelled. Please try again.';
      } else if (err.code === 'auth/invalid-credential' || err.message?.includes('invalid_client')) {
        errorMessage = 'Microsoft sign-in is temporarily unavailable. Please try email login or contact support.';
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account already exists with this email using a different sign-in method. Try signing in with email/password or Google.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (isStorageBlockedError(err)) {
        errorMessage = STORAGE_BLOCKED_MESSAGE;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
      localStorage.removeItem('token');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Update user data
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  // Refresh token
  const refreshToken = async () => {
    if (firebaseUser) {
      const idToken = await firebaseUser.getIdToken(true);
      localStorage.setItem('token', idToken);
      return idToken;
    }
    return null;
  };

  const value = {
    user,
    firebaseUser,
    loading,
    error,
    register,
    login,
    loginWithGoogle,
    loginWithGoogleIdToken,
    loginWithMicrosoft,
    logout,
    updateUser,
    refreshToken,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    emailVerified: firebaseUser?.emailVerified || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
